import {
  CalendarIcon,
  ArrowRightIcon,
  MoonIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'hotel/navigation';
import { times, omitBy, isNull, omit, flatten } from 'lodash';
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useStore } from 'react-redux';

import { PrimaryButton, TealButton } from 'components/Button';
import Card from 'components/Card';
import GroupRequestForm, {
  FormValues,
  calculateFilledGuestCount,
  calculateTotalExpectedGuests,
} from 'components/GroupRequestForm';
import IncompleteGuestListModal from 'components/GroupRequestForm/IncompleteGuestListModal';
import { DoubleRoomSelection } from 'components/GroupRequestForm/RoomRenderers';
import { ClientTeamMemberSelectOption } from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import RoomDetailsSection from 'components/Requests/GroupBookingDetails/RoomDetailsSection';
import {
  AmenityRequirement,
  GuestInput,
  useRequestNewGroupReservationMutation,
} from 'generated/graphql';
import { useClientRequiredFieldsAndPod } from 'hooks/useClientRequiredFieldsAndPod';
import { reset as bookingReset } from 'store/bookingSlice';
import {
  reset as groupDetailsReset,
  RoomConfigurations,
  RoomTypeQuantities,
} from 'store/groupDetailsSlice';
import { type AppState, useAppDispatch, useAppSelector } from 'store/store';
import { selectUnknownGuest } from 'store/unknownGuestSlice';
import { initializeRequiredFieldsArray } from 'utilities/guestFieldMapping';
import { popToast } from 'utilities/popToast';
import removeTypename from 'utilities/removeTypename';

import { calculateHotelNights } from '../../utilities/calculateHotelNights';

const extractGuestFields = (item: ClientTeamMemberSelectOption): GuestInput => {
  const cleanedItem = omitBy(item, isNull);
  return omit(cleanedItem, ['semiUniqueIdentifier', 'unknownGuest']);
};

const YourTrip = ({ formId }: { formId: string }) => {
  const navigate = useNavigate();
  const hotels = useAppSelector((state) => state.groupDetails.shortList);
  const { checkInDate, checkOutDate } = useAppSelector(
    (state) => state.booking,
  );
  const rooms = useAppSelector((state) => state.groupDetails.quantities);
  const roomConfigurations = useAppSelector(
    (state) => state.groupDetails.roomConfigurations,
  );
  const modifySearchParams = useAppSelector(
    (state) => state.groupDetails.searchParams,
  );
  const numSelected = hotels.length;
  const numOfNights = calculateHotelNights(checkInDate, checkOutDate);

  const navToModify = () => {
    navigate(`/search?${modifySearchParams}`);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-backgroundDarkGray bg-white shadow-sm">
      <div className="space-y-10 p-4 sm:p-6">
        <div>
          <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
            Your Trip
          </p>
          <div className="mb-4 border-b border-backgroundDarkGray"></div>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-gray-500">
                Hotels Selected ({numSelected})
              </p>
              <ul className="space-y-2">
                {hotels.map((hotel) => (
                  <li
                    key={hotel.amadeusHotelId}
                    className="font-arimo text-base font-medium text-gray-900"
                  >
                    {hotel.name}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-500">Dates</p>
              <div className="flex items-center gap-2">
                <CalendarIcon className="size-5 text-accentGreen" />
                <p className="font-arimo text-base font-medium text-gray-900">
                  {checkInDate}
                </p>
                <ArrowRightIcon className="size-5 text-gray-900" />
                <p className="font-arimo text-base font-medium text-gray-900">
                  {checkOutDate}
                </p>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-500">
                Total nights
              </p>
              <div className="flex items-center gap-2">
                <MoonIcon className="size-5 text-accentGreen" />
                <p className="font-arimo text-base font-medium text-gray-900">
                  {numOfNights}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="mb-4 font-oswald text-lg font-bold text-gray-900">
            Room Details
          </p>
          <div className="mb-4 border-b border-backgroundDarkGray"></div>
          <div className="space-y-4">
            <RoomDetailsSection
              roomType="single"
              roomTypeLabel="Single"
              configurations={roomConfigurations?.single}
              quantity={rooms.single}
            />

            <RoomDetailsSection
              roomType="double"
              roomTypeLabel="Double"
              configurations={roomConfigurations?.double}
              quantity={rooms.double}
            />

            <RoomDetailsSection
              roomType="suite"
              roomTypeLabel="Suite"
              configurations={roomConfigurations?.suite}
              quantity={rooms.suite}
            />
          </div>
        </div>

        <div className="mt-6 space-y-2">
          <TealButton
            text="Submit Request"
            type="submit"
            form={formId}
            className="w-full"
          />
          <PrimaryButton
            onClick={navToModify}
            className="w-full border border-gray-900 bg-white text-gray-900 hover:bg-gray-100"
          >
            Modify
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const GroupRequest = () => {
  const navigate = useNavigate();
  const email = useAppSelector((state) => state.user.email);
  const {
    quantities: rooms,
    shortList,
    roomConfigurations,
  } = useAppSelector((state) => state.groupDetails);
  const { checkInDate, checkOutDate } = useAppSelector(
    (state) => state.booking,
  );
  const unknownGuest = useAppSelector(selectUnknownGuest);
  const dispatch = useAppDispatch();
  const store = useStore<AppState>();
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();

  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<FormValues | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mounted = useRef(true);
  const completedDraft = useRef<Pick<
    AppState,
    'booking' | 'groupDetails'
  > | null>(null);
  const clearSubmittedDraft = useCallback(
    (draft: Pick<AppState, 'booking' | 'groupDetails'>) => {
      const current = store.getState();
      // A later search or edited room selection belongs to a new draft.
      if (
        current.booking === draft.booking &&
        current.groupDetails === draft.groupDetails
      ) {
        dispatch(groupDetailsReset());
        dispatch(bookingReset());
      }
    },
    [dispatch, store],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Clear the submitted draft after navigation commits. Clearing bookingType
      // while this route is still mounted lets its group-only guard redirect home.
      if (completedDraft.current) clearSubmittedDraft(completedDraft.current);
    };
  }, [clearSubmittedDraft]);

  const [requestNewGroupReservation] = useRequestNewGroupReservationMutation();

  // Calculate actual guest count based on room configurations
  const calculateActualGuestCount = (
    roomConfigurations: RoomConfigurations | undefined,
    rooms: RoomTypeQuantities,
  ): number => {
    let totalGuests = 0;

    // Count guests from room configurations if available
    if (roomConfigurations) {
      totalGuests += roomConfigurations.single.reduce(
        (acc: number, config) => acc + config.rooms * config.guests,
        0,
      );
      totalGuests += roomConfigurations.double.reduce(
        (acc: number, config) => acc + config.rooms * config.guests,
        0,
      );
      totalGuests += roomConfigurations.suite.reduce(
        (acc: number, config) => acc + config.rooms * config.guests,
        0,
      );
    } else {
      // Fallback: count total rooms (not assuming maximum occupancy)
      totalGuests = rooms.single + rooms.double + rooms.suite;
    }

    return totalGuests;
  };

  const initialValues: FormValues = useMemo(() => {
    // Generate initial arrays based on room configurations if available
    const generateInitialArrays = () => {
      if (roomConfigurations) {
        // Calculate total rooms for each type
        const singleTotal = roomConfigurations.single.reduce(
          (acc, config) => acc + config.rooms,
          0,
        );
        const doubleTotal = roomConfigurations.double.reduce(
          (acc, config) => acc + config.rooms,
          0,
        );
        const suiteTotal = roomConfigurations.suite.reduce(
          (acc, config) => acc + config.rooms,
          0,
        );

        return {
          single: times(singleTotal, () => '' as const),
          double: times(doubleTotal, () => '' as const),
          suite: times(suiteTotal, () => '' as const),
        };
      }

      // Fall back to room quantities
      return {
        single: times(rooms.single, () => '' as const),
        double: times(rooms.double, () => '' as const),
        suite: times(rooms.suite, () => '' as const),
      };
    };

    const initialArrays = generateInitialArrays();

    return {
      breakfast: AmenityRequirement.NiceToHave,
      freeParking: AmenityRequirement.NiceToHave,
      kitchen: AmenityRequirement.NiceToHave,
      washerDryer: AmenityRequirement.NiceToHave,
      meetingSpace: AmenityRequirement.NotRequired,
      otherAmenities: '',
      distanceFromDestination: 0,
      brandPreference: '',
      contactEmail: email || '',
      contactPhone: '',
      ...initialArrays,
      quantities: rooms,
      shortList,
      checkInDate,
      guestQuantity: calculateActualGuestCount(
        roomConfigurations,
        rooms,
      ).toString(),
      checkOutDate,
      additionalPreferences: '',
      requiredFields: initializeRequiredFieldsArray(
        clientRequiredFields.map((field) => field.value),
      ),
    };
  }, [
    email,
    rooms,
    clientRequiredFields,
    roomConfigurations,
    checkInDate,
    checkOutDate,
    shortList,
  ]);

  const submitForm = useCallback(
    (values: FormValues) => {
      setIsSubmitting(true);

      const {
        single,
        double,
        suite,
        guestQuantity,
        distanceFromDestination,
        requiredFields,
        ...rest
      } = values;

      const requiredFieldValues = requiredFields
        ? requiredFields.filter(
            (field) => field.value && field.value.trim() !== '',
          )
        : [];

      const nextSingle = flatten(
        single
          .filter((v) => v !== '')
          .map((item) => {
            // Handle both single guest and multi-guest configurations
            if (typeof item === 'object' && 'guest0' in item) {
              // Multiple guests in single room - extract all guests
              const guests = [];
              let guestIndex = 0;
              while (item[`guest${guestIndex}` as keyof typeof item]) {
                guests.push(
                  extractGuestFields(
                    item[
                      `guest${guestIndex}` as keyof typeof item
                    ] as ClientTeamMemberSelectOption,
                  ),
                );
                guestIndex++;
              }
              return guests;
            } else {
              // Single guest in single room
              return extractGuestFields(item as ClientTeamMemberSelectOption);
            }
          }),
      );

      const nextSuite = flatten(
        suite
          .filter((v) => v !== '')
          .map((item) => {
            // Handle both single guest and multi-guest configurations
            if (typeof item === 'object' && 'guest0' in item) {
              // Multiple guests in suite room - extract all guests
              const guests = [];
              let guestIndex = 0;
              while (item[`guest${guestIndex}` as keyof typeof item]) {
                guests.push(
                  extractGuestFields(
                    item[
                      `guest${guestIndex}` as keyof typeof item
                    ] as ClientTeamMemberSelectOption,
                  ),
                );
                guestIndex++;
              }
              return guests;
            } else {
              // Single guest in suite room
              return extractGuestFields(item as ClientTeamMemberSelectOption);
            }
          }),
      ).filter((item): item is NonNullable<typeof item> => item !== null);

      const nextDouble = double
        .filter((v) => v !== '')
        .map((item) => {
          if (typeof item === 'object' && 'firstBed' in item) {
            // Double room with potential 2 guests
            const firstBedGuest = item.firstBed;
            const secondBedGuest = item.secondBed;

            // CRITICAL: Ensure first bed is always populated if any guest exists
            if (!firstBedGuest && secondBedGuest) {
              // Swap: move secondBed guest to firstBed (only guest goes in first bed)
              return {
                firstBed: extractGuestFields(secondBedGuest),
              };
            } else if (firstBedGuest && secondBedGuest) {
              // Both beds filled - include both
              return {
                firstBed: extractGuestFields(firstBedGuest),
                secondBed: extractGuestFields(secondBedGuest),
              };
            } else if (firstBedGuest) {
              // Only first bed filled - don't include secondBed field
              return {
                firstBed: extractGuestFields(firstBedGuest),
              };
            }
            // Both empty - return null (will be filtered out)
            return null;
          } else {
            // Single guest format - treat as firstBed only
            return {
              firstBed: extractGuestFields(
                item as ClientTeamMemberSelectOption,
              ),
            };
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const input = {
        ...rest,
        guestQuantity: Number(guestQuantity),
        distanceFromDestination: Number(distanceFromDestination),
        requiredFields: requiredFieldValues,
        single: removeTypename(nextSingle),
        double: removeTypename(nextDouble),
        suite: removeTypename(nextSuite),
        ...(roomConfigurations
          ? { roomConfigurations: removeTypename(roomConfigurations) }
          : {}),
      };

      // Each request owns the exact draft it submitted, including submissions
      // confirmed through the incomplete-guest modal.
      const { booking, groupDetails } = store.getState();
      const submittedDraft = { booking, groupDetails };
      requestNewGroupReservation({
        variables: { input },
        onCompleted: (data) => {
          if (mounted.current) {
            completedDraft.current = submittedDraft;
            setIsSubmitting(false);
            setShowIncompleteModal(false);
            setPendingFormValues(null);
            navigate(`/requests/${data.requestNewGroupReservation.id}`);
          } else {
            // The user already left this form. Retire its unchanged draft without
            // redirecting their current page or clearing a newer draft.
            clearSubmittedDraft(submittedDraft);
          }
          popToast(`Request successfully submitted!`);
        },
        onError: (error) => {
          if (mounted.current) setIsSubmitting(false);
          popToast(`Error submitting request: ${error.message}`, {
            type: 'error',
          });
        },
      });
    },
    [
      requestNewGroupReservation,
      roomConfigurations,
      store,
      navigate,
      clearSubmittedDraft,
    ],
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      const filledCount = calculateFilledGuestCount(values);
      const totalCount = calculateTotalExpectedGuests(
        roomConfigurations,
        rooms,
      );

      if (filledCount < totalCount) {
        // Guest list is incomplete, show confirmation modal
        setPendingFormValues(values);
        setShowIncompleteModal(true);
      } else {
        // All guests filled, submit directly
        submitForm(values);
      }
    },
    [roomConfigurations, rooms, submitForm],
  );

  const fillEmptyGuestsWithUnknown = useCallback(
    (values: FormValues): FormValues => {
      if (!unknownGuest) {
        return values;
      }

      const unknownGuestOption: ClientTeamMemberSelectOption = {
        id: unknownGuest.id,
        firstName: unknownGuest.firstName,
        lastName: unknownGuest.lastName,
        edipi: unknownGuest.edipi,
        rank: unknownGuest.rank,
        gender: unknownGuest.gender,
        semiUniqueIdentifier: unknownGuest.semiUniqueIdentifier,
        employeeNumber: unknownGuest.employeeNumber,
        mission: unknownGuest.mission,
        jobTitle: unknownGuest.jobTitle,
        taskForce: unknownGuest.taskForce,
        unknownGuest: unknownGuest.unknownGuest,
      };

      const getGuestCountForRoomIndex = (
        config: RoomConfigurations[keyof RoomConfigurations] | undefined,
        index: number,
        defaultCount: number,
      ): number => {
        if (!config) return defaultCount;

        let roomIndex = 0;
        for (const c of config) {
          if (index < roomIndex + c.rooms) {
            return c.guests;
          }
          roomIndex += c.rooms;
        }
        return defaultCount;
      };

      const isEmptySlot = (value: unknown): boolean =>
        value === '' || value === undefined || value === null;

      const fillSingleOrSuiteRoom = (
        guest: ClientTeamMemberSelectOption | '',
        index: number,
        config: RoomConfigurations[keyof RoomConfigurations] | undefined,
      ): ClientTeamMemberSelectOption | '' => {
        const guestCount = getGuestCountForRoomIndex(config, index, 1);

        if (isEmptySlot(guest)) {
          if (guestCount > 1) {
            const multiGuest: Record<string, ClientTeamMemberSelectOption> = {};
            for (let i = 0; i < guestCount; i++) {
              multiGuest[`guest${i}`] = unknownGuestOption;
            }
            return multiGuest as unknown as ClientTeamMemberSelectOption;
          }
          return unknownGuestOption;
        }

        if (typeof guest === 'object' && 'guest0' in guest) {
          const filledGuest: Record<string, ClientTeamMemberSelectOption> = {};
          for (let i = 0; i < guestCount; i++) {
            const key = `guest${i}`;
            const existingValue = (guest as Record<string, unknown>)[key];
            filledGuest[key] = isEmptySlot(existingValue)
              ? unknownGuestOption
              : (existingValue as ClientTeamMemberSelectOption);
          }
          return filledGuest as unknown as ClientTeamMemberSelectOption;
        }

        return guest;
      };

      const fillDoubleRoom = (
        room: DoubleRoomSelection | '',
        index: number,
      ): DoubleRoomSelection | '' => {
        const expectedGuests = getGuestCountForRoomIndex(
          roomConfigurations?.double,
          index,
          2,
        );

        if (isEmptySlot(room)) {
          const filledRoom: DoubleRoomSelection = {
            firstBed: unknownGuestOption,
          };
          if (expectedGuests === 2) {
            filledRoom.secondBed = unknownGuestOption;
          }
          return filledRoom;
        }

        if (typeof room === 'object' && 'firstBed' in room) {
          const filledRoom: DoubleRoomSelection = { ...room };
          if (isEmptySlot(filledRoom.firstBed)) {
            filledRoom.firstBed = unknownGuestOption;
          }
          if (expectedGuests === 2 && isEmptySlot(filledRoom.secondBed)) {
            filledRoom.secondBed = unknownGuestOption;
          }
          return filledRoom;
        }

        return room;
      };

      return {
        ...values,
        single: values.single.map((guest, index) =>
          fillSingleOrSuiteRoom(guest, index, roomConfigurations?.single),
        ),
        suite: values.suite.map((guest, index) =>
          fillSingleOrSuiteRoom(guest, index, roomConfigurations?.suite),
        ),
        double: values.double.map((room, index) => fillDoubleRoom(room, index)),
      };
    },
    [unknownGuest, roomConfigurations],
  );

  const handleConfirmIncomplete = useCallback(() => {
    if (pendingFormValues) {
      const filledValues = fillEmptyGuestsWithUnknown(pendingFormValues);
      submitForm(filledValues);
    }
  }, [pendingFormValues, submitForm, fillEmptyGuestsWithUnknown]);

  const handleCloseModal = useCallback(() => {
    setShowIncompleteModal(false);
    setPendingFormValues(null);
  }, []);

  const filledGuestCount = pendingFormValues
    ? calculateFilledGuestCount(pendingFormValues)
    : 0;
  const totalGuestCount = calculateTotalExpectedGuests(
    roomConfigurations,
    rooms,
  );

  return (
    <div className="mb-24 py-5 lg:mb-60">
      <div className="mx-auto max-w-7xl px-4">
        <div className="mb-4 border-b border-gray-200 pb-5 sm:mb-10">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-900 hover:text-gray-700"
            >
              <ArrowLeftIcon className="size-6" />
            </button>
            <h1 className="font-oswald text-2xl font-bold">Request Details</h1>
          </div>
        </div>
        <div className="flex flex-col gap-12 xl:flex-row">
          <div className="w-full xl:w-3/4">
            <Card className="overflow-visible">
              <GroupRequestForm
                onSubmit={onSubmit}
                rooms={rooms}
                roomConfigurations={roomConfigurations}
                initialValues={initialValues}
                formId={'group-request'}
                hideSubmit={true}
              />
            </Card>
          </div>
          <div className="w-full xl:w-1/3 xl:max-w-md">
            <YourTrip formId={'group-request'} />
          </div>
        </div>
      </div>

      <IncompleteGuestListModal
        isOpen={showIncompleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmIncomplete}
        filledCount={filledGuestCount}
        totalCount={totalGuestCount}
        isProcessing={isSubmitting}
      />
    </div>
  );
};

export default GroupRequest;
